import { formatDateV2 } from "../helpers/dateTimeHelper";
import { DATETIME_FORMATS } from "../constants/datetime-formats";
import { DateTimeRegex } from "../constants/regex";

/**
 * Convert a single datetime string to local time zone using moment-timezone
 * @param {string} dateTimeString The datetime string to convert
 * @returns {string} Converted datetime string in local time zone
 */
function convertDateTimeToLocal(dateTimeString) {
  try {
    let parsedDate;

    // Handle Unix timestamp
    if (DateTimeRegex.UnixTimestamp.test(dateTimeString)) {
      parsedDate = new Date(parseInt(dateTimeString) * 1000);
    }
    // Handle Millisecond timestamp
    else if (DateTimeRegex.MillisecondTimestamp.test(dateTimeString)) {
      parsedDate = new Date(parseInt(dateTimeString));
    }
    // Handle other formats
    else {
      // Check if it's MM/dd/yyyy format (US format)
      const mmddyyyyMatch = dateTimeString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (mmddyyyyMatch) {
        const [, month, day, year, hour, minute, second = '00'] = mmddyyyyMatch;
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);

        // Validate if it's actually MM/dd/yyyy (month should be 1-12, day should be 1-31)
        if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
          // Create UTC date: year-month-day hour:minute:second UTC (treat input as UTC)
          const utcString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute}:${second}Z`;
          parsedDate = new Date(utcString);
        } else if (dayNum >= 1 && dayNum <= 12 && monthNum >= 1 && monthNum <= 31) {
          // If first number is 1-12 and second is 1-31, treat as dd/MM/yyyy (European format)
          const utcString = `${year}-${day.padStart(2, '0')}-${month.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute}:${second}Z`;
          parsedDate = new Date(utcString);
        } else {
          // If both validations fail, default to MM/dd/yyyy format
          const utcString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute}:${second}Z`;
          parsedDate = new Date(utcString);
        }
      } else {
        // For other formats, try parsing as UTC first
        parsedDate = new Date(dateTimeString + ' UTC');
        
        // If that fails, try parsing as local time and then treat as UTC
        if (isNaN(parsedDate.getTime())) {
          const localDate = new Date(dateTimeString);
          if (!isNaN(localDate.getTime())) {
            // Create UTC Date object by treating local time as UTC
            parsedDate = new Date(Date.UTC(
              localDate.getFullYear(),
              localDate.getMonth(),
              localDate.getDate(),
              localDate.getHours(),
              localDate.getMinutes(),
              localDate.getSeconds()
            ));
          }
        }
      }
    }

    // Check if date is valid
    if (isNaN(parsedDate.getTime())) {
      return dateTimeString;
    }

    if (dateTimeString.includes('AM') || dateTimeString.includes('PM')) {
      return formatDateV2(parsedDate, DATETIME_FORMATS.DD_MMM_yyyy_HHmmA);
    } else if (dateTimeString.includes(':')) {
      return formatDateV2(parsedDate, DATETIME_FORMATS.DD_MM_YYYY_HH_MM);
    } else {
      return formatDateV2(parsedDate, DATETIME_FORMATS.DDMMyyyy);
    }
  } catch (error) {
    return dateTimeString;
  }
}

/**
 * Convert UTC datetime strings in HTML message to local time zone
 * @param {string} message HTML message containing datetime strings
 * @returns {string} Processed message with converted datetime strings
 */
export function convertUTCDateTimeInMessage(message) {
  if (!message) return "";
  
  let processedMessage = message;
  
  // Process only once with a single comprehensive pattern
  const allPatterns = DateTimeRegex.AllPatterns;
  const combinedPattern = new RegExp(allPatterns.map(p => p.source).join('|'), 'gi');
  
  processedMessage = processedMessage.replace(combinedPattern, (match) => {
    try {
      return convertDateTimeToLocal(match);
    } catch (error) {
      return match; // Return original datetime if error
    }
  });
  
  return processedMessage;
}
